// src/hooks/useIntervalRefresh.ts
"use client";

import { useCallback, useEffect, useRef, useState } from "react";

interface UseIntervalRefreshOptions {
  offsetMinutes?: number;
  onRefresh: () => Promise<void>;
  enabled?: boolean;
}

interface UseIntervalRefreshReturn {
  nextRefreshAt: Date | null;
  secondsUntilRefresh: number | null;
  isRefreshing: boolean;
  lastRefreshedAt: Date | null;
  isOnline: boolean;
  triggerManualRefresh: () => Promise<void>;
}

function getNextRefreshTime(offsetMinutes: number): Date {
  const now = Date.now();
  const intervalMs = 15 * 60 * 1000;
  const offsetMs = Math.round(offsetMinutes * 60 * 1000);
  const currentBoundaryMs = Math.floor(now / intervalMs) * intervalMs;
  const candidate = currentBoundaryMs + offsetMs;
  return new Date(candidate > now ? candidate : candidate + intervalMs);
}

export function useIntervalRefresh({
  offsetMinutes = 3,
  onRefresh,
  enabled = true,
}: UseIntervalRefreshOptions): UseIntervalRefreshReturn {
  const [nextRefreshAt, setNextRefreshAt] = useState<Date | null>(null);
  const [secondsUntilRefresh, setSecondsUntilRefresh] = useState<number | null>(
    null,
  );
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [isOnline, setIsOnline] = useState(true);

  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  const isRefreshingRef = useRef(false);
  const lastRefreshedAtRef = useRef<Date | null>(null);
  const nextRefreshAtRef = useRef<Date | null>(null);
  // Holds the active setTimeout id so we can cancel + reschedule
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  /* ---- core refresh executor ---- */

  const executeRefresh = useCallback(async () => {
    if (isRefreshingRef.current) return;
    isRefreshingRef.current = true;
    setIsRefreshing(true);
    try {
      await onRefreshRef.current();
      const now = new Date();
      lastRefreshedAtRef.current = now;
      setLastRefreshedAt(now);
    } catch {
      // onRefresh handles its own errors
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, []);

  /* ---- schedule helpers ---- */

  const scheduleNext = useCallback((): Date => {
    const next = getNextRefreshTime(offsetMinutes);
    nextRefreshAtRef.current = next;
    setNextRefreshAt(next);
    return next;
  }, [offsetMinutes]);

  // Cancel any pending timeout and schedule a fresh one
  const reschedule = useCallback(() => {
    if (timeoutIdRef.current !== null) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    if (!enabled) {
      setNextRefreshAt(null);
      setSecondsUntilRefresh(null);
      nextRefreshAtRef.current = null;
      return;
    }

    const next = scheduleNext();
    const delay = Math.max(0, next.getTime() - Date.now());

    timeoutIdRef.current = setTimeout(async () => {
      await executeRefresh();
      reschedule();
    }, delay);
  }, [enabled, scheduleNext, executeRefresh]);

  /* ---- main scheduler — runs on mount and when enabled changes ---- */

  useEffect(() => {
    reschedule();
    return () => {
      if (timeoutIdRef.current !== null) clearTimeout(timeoutIdRef.current);
    };
  }, [reschedule]);

  /* ---- bfcache restoration (browser back/forward button) ---- */

  useEffect(() => {
    const handlePageShow = async (e: PageTransitionEvent) => {
      // e.persisted === true means the page was restored from bfcache.
      // All timers are dead at this point — we must refresh and reschedule.
      if (!e.persisted || !enabled) return;

      await executeRefresh();
      reschedule(); // restart the timeout chain from now
    };

    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [enabled, executeRefresh, reschedule]);

  /* ---- visibilitychange: tab switch or PC sleep/wake ---- */

  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== "visible") return;

      const next = nextRefreshAtRef.current;
      const lastRefresh = lastRefreshedAtRef.current;

      const missedScheduled = next !== null && Date.now() > next.getTime();
      const tooLongSinceRefresh =
        lastRefresh === null ||
        Date.now() - lastRefresh.getTime() > 15 * 60 * 1000;

      if (missedScheduled || tooLongSinceRefresh) {
        await executeRefresh();
        reschedule();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [enabled, executeRefresh, reschedule]);

  /* ---- network online/offline ---- */

  useEffect(() => {
    const handleOnline = async () => {
      setIsOnline(true);
      if (!enabled) return;
      await executeRefresh();
      reschedule();
    };

    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    setIsOnline(navigator.onLine);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [enabled, executeRefresh, reschedule]);

  /* ---- countdown ticker ---- */

  useEffect(() => {
    if (!nextRefreshAt || !enabled) {
      setSecondsUntilRefresh(null);
      return;
    }

    const tick = () => {
      const diff = Math.max(
        0,
        Math.round((nextRefreshAt.getTime() - Date.now()) / 1000),
      );
      setSecondsUntilRefresh(diff);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [nextRefreshAt, enabled]);

  /* ---- manual trigger ---- */

  const triggerManualRefresh = useCallback(async () => {
    await executeRefresh();
    reschedule();
  }, [executeRefresh, reschedule]);

  return {
    nextRefreshAt,
    secondsUntilRefresh,
    isRefreshing,
    lastRefreshedAt,
    isOnline,
    triggerManualRefresh,
  };
}
